import {
  BOSS_INFO, BOSS_PHASE_EXTRA, BOSS_RULE, DEEP_RULE, FAM_INFO, THREAT_RULE, codexTip
} from '../data/codex';
import { TINT } from '../entities/enemy-parts';
import { META, codexStage } from '../platform/storage';
import { codexLore } from '../data/codex-lore';
import { barsHTML } from './codex-bars';
/* ============================================================
   圖鑑右欄：檔案（塗銷式）
   ============================================================

   三段解鎖（使用者 2026-08-02 指定）：沒看過 / 看過沒打死 / 打死過。

   B3 拍板的表現法是**塗銷檔案**：未解鎖的欄位**不消失**，被實心色條塗掉。
   你看得到這份檔案有幾行、看不到內容；沒看過的連名字都塗掉只剩編號。

   ⭐ **三段的版面高度一致**（同樣的區塊、同樣的行數）。舊版是「沒解鎖就少一段」，
   於是右欄在未解鎖時是一大片空白 —— 讀起來像壞掉，而不像「還沒解鎖」。
   高度一致之後，解鎖的動作是「字浮出來」而不是「版面跳一次」。

   為什麼「看過沒打死」要給家族而不是給名字：家族是**形狀**，
   而形狀正是你已經看到的東西。給名字等於給你沒看到的資訊，
   給空白又浪費了你真的已經知道的事。

   ⚠️ 開發模式**不塗銷**（`dev` 為真時 stage 一律當 2）—— 它是驗收台。 */

var $ = function (id) { return document.getElementById(id) as any; };

/** 文案裡的 `**強調**` 轉成 `<b>`。文案是我們自己寫的（`data/codex.ts`），
    所以不需要防注入；需要的是**讓重點在一段 11px 的字裡跳出來** ——
    沒有這一層的話畫面上會直接印出兩個星號（第一版就是這樣）。 */
function md(t) { return String(t).replace(/\*\*(.+?)\*\*/g, '<b>$1</b>'); }

/** 塗銷：把一段字換成等量的實心塊。**長度跟著原文走** ——
    固定長度的塗銷會洩漏「這一段其實很短」或反過來誤導。
    中文一個字算兩格，塗出來的長度才跟原文看起來一樣。 */
function red(t) {
  var n = 0, s = String(t || '');
  for (var i = 0; i < s.length; i++) n += s.charCodeAt(i) > 255 ? 2 : 1;
  n = Math.max(4, Math.min(46, Math.round(n * 0.5)));
  return '<span class="cd-red">' + new Array(n + 1).join('█') + '</span>';
}

/** 一段。`on` 為真時給真的內容，否則塗銷 —— **兩者的行數相同**。 */
function sec(title, body, on) {
  return '<div class="cd-sec"><h4>' + title + '</h4><p>'
    + (on ? md(body) : red(body)) + '</p></div>';
}

/** 這一隻的編號（塗銷態顯示的東西）。家族 + 在家族裡的序，
    例如 `WING-04` —— 它不洩漏身分，但讓每一格有自己的名字可以指認。 */
function codeOf(T) {
  return String(T.boss ? 'BOSS' : T.fam).toUpperCase() + '-'
    + String(Math.abs(hash(T.id)) % 90 + 10);
}
function hash(s) {
  var h = 0;
  for (var i = 0; i < String(s).length; i++) h = (h * 31 + String(s).charCodeAt(i)) | 0;
  return h;
}

/** 右欄。`elite` = 現在看的是精英形式，`stage` = 呼叫端算好的解鎖階段
    （精英與一般版各有各的，見 `ui/codex.ts` 的 `curStage()`）。 */
export function renderData(T, dev, elite?, stage?) {
  var el = $('cData');
  if (!el) return;
  if (!T) { el.innerHTML = ''; return; }
  var st = stage === undefined ? (dev ? 2 : codexStage(T.id)) : stage;
  var full = st === 2, seen = st >= 1;
  var F: any = T.boss ? BOSS_INFO : (FAM_INFO[T.fam] || { zh: T.fam, sil: '', note: '' });

  /* ---------- 抬頭 ---------- */
  var h = '<div class="cd-eyebrow">'
    + (elite ? '精英 · ELITE'
      : T.boss ? (T.deep ? 'BOSS · 深層' : 'BOSS · ' + ((T.stages || 2) + ' 階段'))
        : (T.deep ? '深層 · 第三軸' : '編號 ' + String(T.fam).toUpperCase())) + '</div>';
  h += '<div class="cd-name">' + (full ? T.code : codeOf(T)) + '</div>';
  h += '<div class="cd-role">'
    + (full ? T.zh + ' · ' + T.role : red(T.zh + ' · ' + T.role)) + '</div>';

  /* ---------- 標籤 ---------- */
  h += '<div class="cd-tags">';
  /* 家族是**第二段就給**的（形狀正是你已經看到的東西）。 */
  h += '<span class="cd-tag" style="border-color:#3a4c66;color:#9db6d2">'
    + (T.boss ? 'BOSS' : seen ? '家族 ' + F.zh : '家族 ██') + '</span>';
  if (full) {
    var C = TINT[T.tint] || TINT.red, hex = '#' + C.hex.toString(16).padStart(6, '0');
    h += '<span class="cd-tag" style="border-color:' + hex + ';color:' + hex + '">' + C.zh + '</span>';
  } else {
    h += '<span class="cd-tag lock">威脅 ██</span>';
  }
  if (T.deep) h += '<span class="cd-tag" style="border-color:#6a4fa8;color:#b79cf0">◈ 深層</span>';
  if (elite) h += '<span class="cd-tag" style="border-color:#ffb648;color:#ffd08a">◆ 精英</span>';
  h += '</div>';

  /* ---------- 敘述（第二段就給） ----------
     使用者 2026-08-03 指定：換成**每一隻自己的敘述**（`data/codex-lore.ts`），
     而且**剪影規則直接拿掉**（不是縮小、不是移位）。 */
  var lore = codexLore(T.id);
  h += '<div class="cd-sec"><h4>敘述</h4><p>'
    + (seen ? md(lore) : red(lore)) + '</p></div>';

  /* ---------- 參數 ---------- */
  h += '<div class="cd-sec"><h4>參數</h4>' + barsHTML(T, !full)
    + '<p class="mut" style="margin-top:8px">遭遇 ' + (META.seen[T.id] || 0)
    + ' 次 · 擊破 ' + (full ? (META.killed[T.id] || 0) + ' 次' : '██') + '</p></div>';

  /* ---------- 精英強化 ---------- */
  if (elite) {
    h += sec('精英強化',
      '結構 **×14**、傷害 **×1.4**、體型 **×1.34**，頭上多一圈冠環。'
      + '辨識靠**冠環與亮一階的裝甲**，不是靠大小 —— 20px 下體型讀不出來。', full);
  }

  /* ---------- 威脅通則（只有開發模式） ----------
     使用者 2026-08-03 指定：**威脅通則與應對紀錄只有開發模式看得到**。
     玩家那一側的圖鑑因此是「這是什麼」而不是「怎麼打」——
     打法留給玩家自己在場上學。 */
  if (dev) {
    var R = THREAT_RULE[T.tint];
    if (R) h += sec('威脅通則 · ' + R.zh, R.rule, true);
    if (T.deep) h += sec(DEEP_RULE.zh, DEEP_RULE.rule, true);
  }

  /* ---------- Boss：階段與可破壞部位 ---------- */
  if (T.boss) {
    h += sec(BOSS_RULE.zh, BOSS_RULE.rule, full);
    var ph: string[] = T.pn && T.pn.length ? T.pn
      : (T.p2 ? ['二階段：' + T.p2] : (BOSS_PHASE_EXTRA[T.id] || []));
    if (ph.length) {
      h += '<div class="cd-sec"><h4>階段變化</h4>';
      for (var p = 0; p < ph.length; p++) {
        h += '<p style="margin-top:' + (p ? 6 : 0) + 'px"><b>P' + (p + 1) + '</b> '
          + (full ? md(ph[p]) : red(ph[p])) + '</p>';
      }
      h += '</div>';
    }
    if (T.breakName && T.breakName !== '—') {
      h += '<div class="cd-sec"><h4>可破壞部位</h4><p>'
        + (full ? md(T.breakName) : red(T.breakName))
        + '<br><span class="mut">打亮的地方 —— 拆掉會換掉它的行為。</span></p></div>';
    }
    el.innerHTML = h;
    return;
  }

  /* ---------- 應對紀錄（只有開發模式，同上） ---------- */
  if (dev) h += sec('應對紀錄', codexTip(T.id), true);
  if (!full) {
    h += '<div class="cd-lock">'
      + (seen ? '擊破一次之後解鎖上面塗銷的部分。' : '還沒有遭遇紀錄。圖鑑只記錄你親眼見過的東西。')
      + '</div>';
  }
  el.innerHTML = h;
}
